import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Sparkles, MessageSquare, Eye, RotateCcw, History, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { invalidateTenantAIConfig } from '@/hooks/useTenantAIConfig';
import UatuKbAdmin from './UatuKbAdmin';

const LLM_OPTIONS = [
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8 (⭐ Meilleur raisonnement)', provider: 'anthropic' },
    { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', provider: 'anthropic' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (rapide, équilibré)', provider: 'anthropic' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (économique)', provider: 'anthropic' },
    { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', provider: 'openai' },
    { value: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai' },
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', provider: 'gemini' },
];

const FIELD_LABELS = {
    doctrine: 'Doctrine',
    tone: 'Ton',
    kpi_focus: 'KPIs prioritaires',
    modules: 'Modules',
    assistant_name: 'Nom',
    assistant_tagline: 'Tagline',
    sector: 'Secteur',
};

const MODE_BADGES = {
    append: { label: 'Ajout', className: 'bg-emerald-100 text-emerald-700' },
    replace: { label: 'Remplacement', className: 'bg-amber-100 text-amber-700' },
    revert: { label: 'Annulation', className: 'bg-slate-200 text-slate-700' },
};

const formatShort = (v) => {
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v)) return v.join(' · ');
    const s = String(v).replace(/\n/g, ' ⏎ ');
    return s.length > 140 ? s.slice(0, 140) + '…' : s;
};

const relTime = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return 'à l\'instant';
        if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
        if (diff < 86400 * 7) return `il y a ${Math.round(diff / 86400)} j`;
        return d.toLocaleDateString('fr-FR');
    } catch { return ''; }
};

/**
 * Back-office paramétrage de l'IA multi-tenant.
 * Un admin_full édite ici : nom, tagline, ton, secteur, modules, KPIs, doctrine,
 * modèle LLM. Un bouton "Test conversation" permet de valider les modifs à chaud.
 *
 * Ce même formulaire servira à onboarder tout futur client SaaS.
 */
export function TenantAIConfigAdmin({ kbFocusDocId } = {}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState(null);
    const [testMessage, setTestMessage] = useState('Résume-moi mes priorités du jour.');
    const [testReply, setTestReply] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [reverting, setReverting] = useState(null);

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const res = await api.get('/tenant/ai-config/doctrine-history');
            setHistory(res.data?.items || []);
        } catch { /* silent */ }
        finally { setHistoryLoading(false); }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/tenant/ai-config');
                setConfig(res.data);
            } catch (e) {
                toast.error("Impossible de charger la configuration IA");
            } finally {
                setLoading(false);
            }
            loadHistory();
        })();
    }, [loadHistory]);

    const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
    const setListField = (key, value) => setField(key, value.split('\n').map((s) => s.trim()).filter(Boolean));

    const save = async () => {
        setSaving(true);
        try {
            const provider = LLM_OPTIONS.find((o) => o.value === config.llm_model)?.provider || 'anthropic';
            const payload = {
                assistant_name: config.assistant_name,
                assistant_tagline: config.assistant_tagline,
                brand_name: config.brand_name,
                sector: config.sector,
                tone: config.tone,
                modules: config.modules || [],
                kpi_focus: config.kpi_focus || [],
                doctrine: config.doctrine,
                llm_model: config.llm_model,
                provider,
            };
            const res = await api.patch('/tenant/ai-config', payload);
            setConfig(res.data);
            invalidateTenantAIConfig();
            toast.success(`Configuration de ${res.data.assistant_name} sauvegardée`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        if (!testMessage.trim()) return;
        setTesting(true);
        setTestReply(null);
        try {
            const res = await api.post('/ai/chat', { message: testMessage.trim() });
            setTestReply(res.data.reply);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors du test');
        } finally {
            setTesting(false);
        }
    };

    const revertUpdate = async (item) => {
        if (item.reverted) return;
        if (!window.confirm(`Annuler cette modification de « ${FIELD_LABELS[item.field] || item.field} » ?\n\nUatu retrouvera l'ancienne valeur.`)) {
            return;
        }
        setReverting(item.id);
        try {
            const res = await api.post(`/tenant/ai-config/doctrine-history/${item.id}/revert`);
            if (res.data?.config) {
                setConfig(res.data.config);
                invalidateTenantAIConfig();
            }
            toast.success('Modification annulée. La doctrine est revenue à l\'état précédent.');
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Impossible d\'annuler');
        } finally {
            setReverting(null);
        }
    };

    if (loading || !config) {
        return (
            <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="tenant-ai-config-page">
            {/* Header */}
            <div className="rounded-2xl bg-gradient-to-r from-violet-600 via-violet-500 to-purple-600 text-white p-5">
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold">IA & Assistant — {config.assistant_name}</h2>
                        <p className="text-sm text-white/85 mt-1">
                            Configure ton assistant IA pour ton secteur. Ces réglages pilotent le ton, la doctrine et le modèle de <strong>{config.assistant_name}</strong> pour tous les utilisateurs de {config.brand_name}.
                        </p>
                    </div>
                </div>
            </div>

            {/* Identité */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Identité de l&apos;assistant</CardTitle>
                    <CardDescription>Le nom, le sous-titre et la marque affichés dans le CRM.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>Nom de l&apos;assistant</Label>
                            <Input
                                value={config.assistant_name || ''}
                                onChange={(e) => setField('assistant_name', e.target.value)}
                                placeholder="Ex: Argus, Axi, Zoe…"
                                data-testid="ai-config-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Nom de la marque / société</Label>
                            <Input
                                value={config.brand_name || ''}
                                onChange={(e) => setField('brand_name', e.target.value)}
                                data-testid="ai-config-brand"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Tagline / sous-titre</Label>
                        <Input
                            value={config.assistant_tagline || ''}
                            onChange={(e) => setField('assistant_tagline', e.target.value)}
                            placeholder="Ex: L'œil du CRM · Il voit tout, il oublie rien"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Secteur d&apos;activité</Label>
                        <Input
                            value={config.sector || ''}
                            onChange={(e) => setField('sector', e.target.value)}
                            placeholder="Ex: Courtage énergie · Transaction immo · Cabinet compta…"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Ton & style */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Ton & style</CardTitle>
                    <CardDescription>Décris à Argus comment il doit s&apos;exprimer.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Textarea
                        value={config.tone || ''}
                        onChange={(e) => setField('tone', e.target.value)}
                        rows={4}
                        placeholder="Ex: Tutoiement, direct, chiffré, sans blabla. 5-6 lignes max."
                        data-testid="ai-config-tone"
                    />
                </CardContent>
            </Card>

            {/* Modules & KPI */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Modules du CRM</CardTitle>
                        <CardDescription>Un module par ligne.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={(config.modules || []).join('\n')}
                            onChange={(e) => setListField('modules', e.target.value)}
                            rows={5}
                            placeholder="Courtage (Easy Power)&#10;Travaux d'optimisation (Chantier)"
                        />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">KPIs prioritaires</CardTitle>
                        <CardDescription>Ce qu&apos;Argus doit surveiller en premier. Un KPI par ligne.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={(config.kpi_focus || []).join('\n')}
                            onChange={(e) => setListField('kpi_focus', e.target.value)}
                            rows={5}
                            placeholder="Conversion mandat → gagné&#10;Dossiers silencieux >10j"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Doctrine */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Doctrine métier</CardTitle>
                    <CardDescription>Tes règles internes. Utilise le préfixe ▸ ou - pour les puces.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Textarea
                        value={config.doctrine || ''}
                        onChange={(e) => setField('doctrine', e.target.value)}
                        rows={10}
                        className="font-mono text-sm"
                        placeholder="▸ Un dossier silencieux >10j = alerte rouge&#10;▸ Rythme de relance : J+3, J+7, J+14"
                        data-testid="ai-config-doctrine"
                    />
                    <p className="text-[11px] text-slate-500 mt-2">
                        Cette doctrine est injectée à chaque conversation. Plus elle est riche et spécifique, plus {config.assistant_name} devient précis.
                    </p>
                </CardContent>
            </Card>

            {/* Modèle */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Modèle LLM</CardTitle>
                    <CardDescription>Le cerveau de {config.assistant_name}. Change-le à chaud.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Select value={config.llm_model} onValueChange={(v) => setField('llm_model', v)}>
                        <SelectTrigger data-testid="ai-config-llm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {LLM_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    <span className="flex items-center gap-2">
                                        {o.label}
                                        <Badge variant="secondary" className="text-[10px] uppercase">{o.provider}</Badge>
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-3 shadow-lg">
                <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-violet-600 hover:bg-violet-700 gap-2"
                    data-testid="ai-config-save-btn"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Sauvegarder
                </Button>
                <Button
                    variant="outline"
                    onClick={() => { window.location.reload(); }}
                    disabled={saving}
                    className="gap-2"
                >
                    <RotateCcw className="w-4 h-4" /> Recharger sans sauver
                </Button>
                <span className="ml-auto text-[11px] text-slate-500 italic hidden sm:inline">
                    Les changements s&apos;appliquent immédiatement après sauvegarde.
                </span>
            </div>

            {/* Base de connaissances Uatu (RAG) */}
            <UatuKbAdmin focusDocId={kbFocusDocId} />

            {/* Historique doctrine — évolutions faites via chat Uatu */}
            <Card className="border-violet-200">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-4 h-4 text-violet-600" />
                        Doctrine vivante — historique des évolutions
                        {history.length > 0 && (
                            <Badge variant="secondary" className="text-[10px]">{history.length}</Badge>
                        )}
                    </CardTitle>
                    <CardDescription>
                        Chaque règle ajoutée à {config.assistant_name} par toi (via chat ou formulaire) est tracée ici. Tu peux annuler une modification à tout moment.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {historyLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                            <Loader2 className="w-4 h-4 animate-spin" /> Chargement de l&apos;historique…
                        </div>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-slate-400 italic py-4">
                            Aucune modification enregistrée pour l&apos;instant. Discute avec {config.assistant_name} et demande-lui d&apos;ajouter des règles — elles apparaîtront ici.
                        </p>
                    ) : (
                        <div className="space-y-2" data-testid="doctrine-history-list">
                            {history.map((item) => {
                                const badge = MODE_BADGES[item.mode] || MODE_BADGES.append;
                                const isRevert = item.mode === 'revert';
                                return (
                                    <div
                                        key={item.id || item.created_at}
                                        data-testid={`doctrine-history-item-${item.id}`}
                                        className={`rounded-lg border p-3 ${item.reverted ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200'}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center flex-wrap gap-2 mb-1">
                                                    <span className="text-sm font-semibold text-slate-800">
                                                        {FIELD_LABELS[item.field] || item.field}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${badge.className}`}>
                                                        {badge.label}
                                                    </span>
                                                    <span className="text-[11px] text-slate-500">
                                                        {relTime(item.created_at)} · {item.user_email || 'inconnu'}
                                                    </span>
                                                    {item.reverted && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-300 text-slate-700 font-semibold">
                                                            Annulée
                                                        </span>
                                                    )}
                                                </div>
                                                {item.reason && (
                                                    <p className="text-[12px] text-slate-600 italic mb-1.5">
                                                        « {item.reason} »
                                                    </p>
                                                )}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                                                    <div className="rounded bg-red-50 border border-red-100 px-2 py-1">
                                                        <p className="text-[10px] uppercase font-semibold text-red-700 mb-0.5">Avant</p>
                                                        <p className="text-slate-700 font-mono break-words">{formatShort(item.old_value)}</p>
                                                    </div>
                                                    <div className="rounded bg-emerald-50 border border-emerald-100 px-2 py-1">
                                                        <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-0.5">Après</p>
                                                        <p className="text-slate-700 font-mono break-words">{formatShort(item.new_value)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            {!item.reverted && !isRevert && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="gap-1 shrink-0"
                                                    disabled={reverting === item.id}
                                                    onClick={() => revertUpdate(item)}
                                                    data-testid={`doctrine-revert-${item.id}`}
                                                >
                                                    {reverting === item.id ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <Undo2 className="w-3.5 h-3.5" />
                                                    )}
                                                    Annuler
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Test conversation */}
            <Card className="border-violet-200">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-violet-600" />
                        Tester {config.assistant_name}
                    </CardTitle>
                    <CardDescription>Envoie un message test pour valider ta configuration (utilise ta config actuellement sauvegardée).</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <Textarea
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        rows={2}
                        placeholder="Écris un message test…"
                        data-testid="ai-config-test-input"
                    />
                    <Button
                        onClick={runTest}
                        disabled={testing || !testMessage.trim()}
                        variant="outline"
                        className="gap-2"
                        data-testid="ai-config-test-btn"
                    >
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        Envoyer le test
                    </Button>
                    {testReply && (
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 whitespace-pre-wrap text-sm text-slate-800" data-testid="ai-config-test-reply">
                            <p className="text-[10px] font-semibold uppercase text-violet-600 mb-2">Réponse de {config.assistant_name}</p>
                            {testReply}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default TenantAIConfigAdmin;
